import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Icon } from '@iconify/react'
import {
  fetchMyAffiliateBalanceTransactions,
  fetchMyAffiliatePrograms,
  formatMyAffiliateCurrency,
  manualPrograms,
} from '@/lib/my-affiliate'
import type { MyAffiliateBalanceTransaction, MyAffiliateProgram } from '@/types/my-affiliate'
import styles from './SouAfiliadoPage.module.css'

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function transactionLabel(type: MyAffiliateBalanceTransaction['type']): string {
  switch (type) {
    case 'commission_credit':
      return 'Crédito de comissão'
    case 'payout_debit':
      return 'Baixa de saldo'
    case 'simulation_credit':
      return 'Crédito simulado'
    default:
      return type
  }
}

export function SouAfiliadoSaldoPage() {
  const [searchParams] = useSearchParams()
  const programParam = searchParams.get('program_id')

  const [programs, setPrograms] = useState<MyAffiliateProgram[]>([])
  const [selectedId, setSelectedId] = useState<number | ''>('')
  const [transactions, setTransactions] = useState<MyAffiliateBalanceTransaction[]>([])
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState<{
    current_page: number
    last_page: number
    total: number
  } | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const manual = useMemo(() => manualPrograms(programs), [programs])

  const selectedProgram = useMemo(
    () => (selectedId === '' ? null : programs.find((p) => p.affiliate_id === selectedId) ?? null),
    [programs, selectedId]
  )

  const loadLedger = useCallback(async (affiliateId: number, p: number) => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await fetchMyAffiliateBalanceTransactions(affiliateId, {
        page: p,
        per_page: 20,
      })
      setTransactions(res.items)
      setPagination({
        current_page: res.pagination.current_page,
        last_page: res.pagination.last_page,
        total: res.pagination.total,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar extrato.')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      setIsLoading(true)
      setError(null)
      try {
        const res = await fetchMyAffiliatePrograms()
        if (cancelled) return
        setPrograms(res.programs)
        const manualList = manualPrograms(res.programs)
        if (manualList.length === 0) {
          setSelectedId('')
          setIsLoading(false)
          return
        }
        const fromParam = programParam ? Number.parseInt(programParam, 10) : NaN
        if (Number.isFinite(fromParam) && manualList.some((p) => p.affiliate_id === fromParam)) {
          setSelectedId(fromParam)
        } else {
          setSelectedId(manualList[0].affiliate_id)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Erro ao carregar programas.')
          setIsLoading(false)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [programParam])

  useEffect(() => {
    if (selectedId === '') {
      setTransactions([])
      setPagination(null)
      setIsLoading(false)
      return
    }
    void loadLedger(selectedId, page)
  }, [selectedId, page, loadLedger])

  if (!isLoading && manual.length === 0 && !error) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:wallet-outline" style={{ fontSize: '2rem' }} />
        <p>
          Você não possui programas com pagamento manual de comissão.
          <br />
          Nos programas com pagamento automático (Doey), a comissão é repassada no split do PIX.
        </p>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.filterRow}>
        <label className={styles.filterLabel} htmlFor="saldo-program-select">
          Programa
        </label>
        <select
          id="saldo-program-select"
          className={styles.select}
          value={selectedId === '' ? '' : String(selectedId)}
          onChange={(e) => {
            const v = e.target.value
            setSelectedId(v === '' ? '' : Number.parseInt(v, 10))
            setPage(1)
          }}
        >
          {manual.map((p) => (
            <option key={p.affiliate_id} value={p.affiliate_id}>
              {p.institution.name}
            </option>
          ))}
        </select>
      </div>

      {selectedProgram ? (
        <div className={styles.statsGridSecondary}>
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Saldo disponível</h3>
            <p style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, margin: 0 }}>
              {formatMyAffiliateCurrency(selectedProgram.balance)}
            </p>
            <p className={styles.programMeta} style={{ marginTop: 'var(--spacing-sm)' }}>
              A baixa do saldo é registrada pela instituição após o repasse externo.
            </p>
          </div>
          <div className={styles.card}>
            <h3 className={styles.cardTitle}>Comissões acumuladas</h3>
            <p style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, margin: 0 }}>
              {formatMyAffiliateCurrency(selectedProgram.metrics.total_commission)}
            </p>
            <p className={styles.programMeta} style={{ marginTop: 'var(--spacing-sm)' }}>
              {selectedProgram.metrics.payments_count} venda(s) pagas atribuídas
            </p>
          </div>
        </div>
      ) : null}

      {error ? (
        <div className={styles.centered}>
          <span>{error}</span>
        </div>
      ) : isLoading ? (
        <div className={styles.centered}>
          <Icon icon="mdi:loading" className={styles.spinner} />
        </div>
      ) : (
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>Extrato</h3>
          {transactions.length === 0 ? (
            <div className={styles.emptyBlock}>
              <span>Nenhuma movimentação no extrato.</span>
            </div>
          ) : (
            <>
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Tipo</th>
                      <th>Valor</th>
                      <th>Observação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((t) => (
                      <tr key={t.id}>
                        <td>{formatDate(t.created_at)}</td>
                        <td>{transactionLabel(t.type)}</td>
                        <td>
                          {t.type === 'payout_debit' ? '−' : '+'}
                          {formatMyAffiliateCurrency(t.amount)}
                        </td>
                        <td>{t.note ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {pagination && pagination.last_page > 1 ? (
                <div className={styles.pagination}>
                  <button
                    type="button"
                    className={styles.pageBtn}
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Anterior
                  </button>
                  <span>
                    Página {pagination.current_page} de {pagination.last_page}
                  </span>
                  <button
                    type="button"
                    className={styles.pageBtn}
                    disabled={page >= pagination.last_page}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Próxima
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>
      )}
    </div>
  )
}
